import type { LogMeta } from "./logger";
import { isTagValue, type TagValue } from "@/packages/o11y/metrics/tags";

// Every capture is logged with `captured: true`, the marker error tracking alerts filter on.

// A message capture is for a condition worth tracking that has no Error and should not alert.
export type CaptureMessageLevel = "warn" | "info";

/** `tags` are bounded dimensions (the metrics `Tag` objects); `context` is the ambient fields plus child-logger bindings. */
type CaptureFields = {
  attributes: LogMeta;
  tags: readonly TagValue[];
  context: LogMeta;
};

export type CaptureRecord =
  | (CaptureFields & { kind: "exception"; error: unknown })
  | (CaptureFields & {
      kind: "message";
      message: string;
      level: CaptureMessageLevel;
    });

export type CaptureSink = (record: CaptureRecord) => void;

export const CAPTURED_EXCEPTION_MESSAGE = "captured exception";

/** Tags as plain fields, so a log attribute matches the metric tag of the same key. */
export const tagsToFields = (tags: readonly TagValue[]): LogMeta =>
  Object.fromEntries(tags.map(({ key, value }) => [key, value]));

// TODO(soon): blank secret-named fields (password, token, secret), as Sentry's scrubber does.
// TODO(soon): truncate long strings and deep nesting, as the Sentry SDK does.
export const captureToLogMeta = ({
  attributes,
  tags,
  context,
}: CaptureRecord): LogMeta => ({
  ...context,
  ...tagsToFields(tags),
  ...attributes,
  captured: true,
});

export const splitCaptureArgs = (
  args: readonly (LogMeta | TagValue)[]
): { attributes: LogMeta; tags: readonly TagValue[] } => {
  const [first, ...rest] = args;
  if (first === undefined || isTagValue(first)) {
    return { attributes: {}, tags: args.filter(isTagValue) };
  }
  return { attributes: first, tags: rest.filter(isTagValue) };
};
